import { useCallback, useEffect, useRef, useState } from 'react'

type SaveStatus = 'saved' | 'saving' | 'dirty' | 'error'

export function useScriptAutoSave(opts: {
  enabled: boolean
  dirty: boolean
  onSave: () => Promise<void>
  delayMs?: number
}) {
  const { enabled, dirty, onSave, delayMs = 1800 } = opts
  const [status, setStatus] = useState<SaveStatus>('saved')
  const timerRef = useRef<number | null>(null)
  const savingRef = useRef(false)
  const inflightRef = useRef<Promise<boolean> | null>(null)

  const runSave = useCallback(async (): Promise<boolean> => {
    if (!enabled || !dirty) return true
    savingRef.current = true
    setStatus('saving')
    try {
      await onSave()
      setStatus('saved')
      return true
    } catch {
      setStatus('error')
      return false
    } finally {
      savingRef.current = false
    }
  }, [enabled, dirty, onSave])

  const flushSave = useCallback(async (): Promise<boolean> => {
    if (inflightRef.current) {
      return inflightRef.current
    }
    const task = runSave()
    inflightRef.current = task
    try {
      return await task
    } finally {
      if (inflightRef.current === task) {
        inflightRef.current = null
      }
    }
  }, [runSave])

  useEffect(() => {
    if (!enabled) return
    if (!dirty) {
      setStatus((prev) => (prev === 'saving' ? prev : 'saved'))
      return
    }
    setStatus('dirty')
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      void flushSave()
    }, delayMs)
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
    }
  }, [enabled, dirty, delayMs, flushSave])

  return { status, flushSave }
}
